/**
 * @description Cross-tab refresh coordination for Wristband refresh-token rotation.
 *
 * Key features:
 * - One tab at a time calls POST /auth/refresh (navigator.locks, else
 *   BroadcastChannel + a storage lock)
 * - Sibling tabs wait and reuse that result instead of rotating again
 * - 5xx, timeout, and network errors retry with a short backoff
 * - Only a definitive 401 clears the session: AUTH_NO_REFRESH_TOKEN,
 *   AUTH_REFRESH_FAILED, or Wristband invalid_grant
 *
 * @dependencies
 * - None. Callers pass the POST function, storage, and lock implementation.
 *
 * @notes
 * - Per-tab in-flight dedupe stays in AuthContext refreshPromiseRef.
 * - Sibling reuse is the Web Locks callback return plus a BroadcastChannel
 *   message to tabs that are already waiting. The access token is never
 *   written to localStorage. A reload takes the httpOnly refresh cookie
 *   under the same lock.
 * - The fallback storage key is only a lock owner id, not a token.
 * - Do not treat a transient error as a terminal 401. Do not log the token.
 */

export const AUTH_REFRESH_LOCK_NAME = 'lynx-auth-refresh';
export const AUTH_REFRESH_LOCK_KEY = 'lynx-auth-refresh-lock';

/** Initial attempt plus two retries. Backoff is between attempts, not before the first. */
export const REFRESH_MAX_RETRIES = 2;
export const REFRESH_BACKOFF_MS = [400, 800];

/** Fallback only — Web Locks holds the real lock until the refresh settles. */
export const FALLBACK_LOCK_TTL_MS = 45000;
export const FALLBACK_WAIT_MS = 40000;

const DEFINITIVE_REFRESH_CODES = new Set([
  'AUTH_NO_REFRESH_TOKEN',
  'AUTH_REFRESH_FAILED'
]);

const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
let sharedChannel;

/**
 * One BroadcastChannel per page. Same-name channels deliver across tabs.
 *
 * @returns {BroadcastChannel|null}
 */
function getSharedChannel() {
  if (sharedChannel !== undefined) return sharedChannel;
  if (typeof BroadcastChannel === 'undefined') {
    sharedChannel = null;
    return null;
  }
  try {
    sharedChannel = new BroadcastChannel(AUTH_REFRESH_LOCK_NAME);
  } catch {
    sharedChannel = null;
  }
  return sharedChannel;
}

/**
 * Wristband OAuth invalid_grant, including a nested error object.
 *
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
function isInvalidGrantBody(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.error === 'invalid_grant' || data.error_code === 'invalid_grant' || data.code === 'invalid_grant') {
    return true;
  }
  const nested = data.error;
  if (nested && typeof nested === 'object') {
    return nested.code === 'invalid_grant' || nested.error === 'invalid_grant';
  }
  return false;
}

/**
 * True only for a terminal refresh rejection.
 * Missing cookie, refresh failed, or Wristband invalid_grant (400 or 401).
 *
 * @param {object|null|undefined} error - Axios-shaped error
 * @returns {boolean}
 */
export function isDefinitiveRefreshFailure(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (isInvalidGrantBody(data) && (status === 400 || status === 401)) return true;
  if (status !== 401) return false;
  const code = (data && typeof data.error === 'object' && data.error.code) || data?.code;
  return DEFINITIVE_REFRESH_CODES.has(code);
}

/**
 * True for a refresh the caller should retry: HTTP 5xx, timeout, or network.
 * A definitive 401 is never transient.
 *
 * @param {object|null|undefined} error - Axios-shaped error
 * @returns {boolean}
 */
export function isTransientRefreshFailure(error) {
  if (!error || typeof error !== 'object') return false;
  if (isDefinitiveRefreshFailure(error)) return false;

  const status = error.response?.status;
  if (typeof status === 'number' && status >= 500) return true;
  if (error.response) return false;

  const code = error.code;
  if (
    code === 'ECONNABORTED'
    || code === 'ETIMEDOUT'
    || code === 'ERR_NETWORK'
    || code === 'ECONNRESET'
    || code === 'ENOTFOUND'
    || code === 'ECONNREFUSED'
  ) {
    return true;
  }

  if (typeof error.message === 'string' && /timeout|network/i.test(error.message)) return true;
  if (error.request) return true;
  return false;
}

/**
 * POST /auth/refresh with a short backoff. Does not clear session state.
 *
 * @param {object} options
 * @param {function(): Promise<object>} options.postRefresh
 * @param {function(number): Promise<void>} [options.sleep]
 * @param {number} [options.maxRetries]
 * @param {number[]} [options.backoffMs]
 * @returns {Promise<{ok: boolean, definitive: boolean, accessToken: string|null, expiresIn: number|null, error?: object}>}
 */
export async function executeRefreshWithRetry({
  postRefresh,
  sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
  maxRetries = REFRESH_MAX_RETRIES,
  backoffMs = REFRESH_BACKOFF_MS
} = {}) {
  const attempts = maxRetries + 1;
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await postRefresh();
      const accessToken = response?.data?.accessToken;
      const expiresIn = response?.data?.expiresIn;
      if (response?.data?.success && accessToken) {
        return {
          ok: true,
          definitive: false,
          accessToken,
          expiresIn: expiresIn ?? null
        };
      }
      return { ok: false, definitive: false, accessToken: null, expiresIn: null };
    } catch (error) {
      if (isDefinitiveRefreshFailure(error)) {
        return {
          ok: false,
          definitive: true,
          accessToken: null,
          expiresIn: null,
          error
        };
      }
      lastError = error;
      const canRetry = isTransientRefreshFailure(error) && attempt < attempts - 1;
      if (!canRetry) {
        return {
          ok: false,
          definitive: false,
          accessToken: null,
          expiresIn: null,
          error
        };
      }
      const delay = backoffMs[Math.min(attempt, backoffMs.length - 1)] ?? 800;
      await sleep(delay);
    }
  }

  return {
    ok: false,
    definitive: false,
    accessToken: null,
    expiresIn: null,
    error: lastError
  };
}

/**
 * @param {string|null|undefined} raw
 * @returns {object|null}
 */
function safeParse(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * A BroadcastChannel payload from a sibling tab. Not read from storage.
 *
 * @param {object|null|undefined} data
 * @returns {object|null}
 */
function reusableFromChannel(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.ok === true && data.accessToken) {
    return {
      ok: true,
      definitive: false,
      accessToken: data.accessToken,
      expiresIn: data.expiresIn ?? null
    };
  }
  if (data.definitive === true) {
    return { ok: false, definitive: true, accessToken: null, expiresIn: null };
  }
  if (data.ok === false) {
    return { ok: false, definitive: false, accessToken: null, expiresIn: null };
  }
  return null;
}

/**
 * Listen before taking the lock so a sibling's message arrives while we wait.
 *
 * @param {BroadcastChannel|null|undefined} channel
 * @returns {{ current: function(): object|null, stop: function(): void }}
 */
function listenForPeer(channel) {
  let peer = null;
  const onMessage = (event) => {
    const reusable = reusableFromChannel(event?.data);
    if (reusable) peer = reusable;
  };
  channel?.addEventListener?.('message', onMessage);
  return {
    current: () => peer,
    stop() {
      channel?.removeEventListener?.('message', onMessage);
    }
  };
}

/**
 * Tell waiting tabs the outcome. The sender does not receive its own message.
 * Nothing is written to localStorage.
 *
 * @param {BroadcastChannel|null|undefined} channel
 * @param {object} result
 */
function announce(channel, result) {
  const payload = {
    ok: !!result?.ok,
    definitive: !!result?.definitive,
    accessToken: result?.ok ? (result.accessToken || null) : null,
    expiresIn: result?.ok ? (result.expiresIn ?? null) : null
  };
  try {
    channel?.postMessage?.(payload);
  } catch {
    // A detached BroadcastChannel must not fail the refresh.
  }
}

/**
 * @param {object} env
 * @returns {boolean}
 */
function tryAcquireFallbackLock(env) {
  const storage = env.storage;
  if (!storage) return true;

  const now = env.now();
  const ttl = env.fallbackLockTtlMs ?? FALLBACK_LOCK_TTL_MS;
  const current = safeParse(storage.getItem(AUTH_REFRESH_LOCK_KEY));
  if (current && current.owner !== env.tabId && now - current.at < ttl) {
    return false;
  }

  try {
    storage.setItem(AUTH_REFRESH_LOCK_KEY, JSON.stringify({ owner: env.tabId, at: now }));
  } catch {
    return true;
  }

  const confirmed = safeParse(storage.getItem(AUTH_REFRESH_LOCK_KEY));
  return confirmed?.owner === env.tabId;
}

/**
 * @param {object} env
 */
function releaseFallbackLock(env) {
  try {
    const current = safeParse(env.storage?.getItem(AUTH_REFRESH_LOCK_KEY));
    if (current?.owner === env.tabId) {
      env.storage.removeItem(AUTH_REFRESH_LOCK_KEY);
    }
  } catch {
    // Leaving a stale lock is recovered by FALLBACK_LOCK_TTL_MS.
  }
}

/**
 * Wait until a sibling BroadcastChannel message arrives.
 *
 * @param {{ current: function(): object|null }} heard
 * @param {object} env
 * @returns {Promise<object|null>}
 */
function waitForPeer(heard, env) {
  if (env.waitForResult) return env.waitForResult(env.fallbackWaitMs ?? FALLBACK_WAIT_MS);

  const deadline = env.now() + (env.fallbackWaitMs ?? FALLBACK_WAIT_MS);
  const poll = async () => {
    let spins = 0;
    while (heard.current() == null && env.now() < deadline && spins < 1000) {
      await env.sleep(env.pollIntervalMs ?? 50);
      spins += 1;
    }
    return heard.current();
  };
  return poll();
}

/**
 * Run the refresh, or reuse a sibling message that arrived while we waited.
 *
 * @param {function(): Promise<object>} task
 * @param {object} env
 * @param {{ current: function(): object|null }} heard
 * @returns {Promise<object>}
 */
async function runLeader(task, env, heard) {
  // Yield once so a BroadcastChannel task queued by the previous holder
  // lands before we decide to POST.
  await env.sleep(0);
  const peer = heard.current();
  if (peer) return { ...peer, reused: true };
  const result = await task();
  announce(env.channel, result);
  return result;
}

/**
 * @param {function(): Promise<object>} task
 * @param {object} env
 * @param {{ current: function(): object|null }} heard
 * @returns {Promise<object>}
 */
async function withBroadcastAndStorageFallback(task, env, heard) {
  const early = heard.current();
  if (early) return { ...early, reused: true };

  if (tryAcquireFallbackLock(env)) {
    try {
      return await runLeader(task, env, heard);
    } finally {
      releaseFallbackLock(env);
    }
  }

  const peer = await waitForPeer(heard, env);
  if (peer) return { ...peer, reused: true };

  if (tryAcquireFallbackLock(env)) {
    try {
      return await runLeader(task, env, heard);
    } finally {
      releaseFallbackLock(env);
    }
  }

  const late = heard.current();
  if (late) return { ...late, reused: true };
  const result = await task();
  announce(env.channel, result);
  return result;
}

/**
 * @param {object} [overrides]
 * @returns {object}
 */
function resolveEnv(overrides = {}) {
  const locks = Object.prototype.hasOwnProperty.call(overrides, 'locks')
    ? overrides.locks
    : (typeof navigator !== 'undefined' ? navigator.locks : undefined);

  const channel = Object.prototype.hasOwnProperty.call(overrides, 'channel')
    ? overrides.channel
    : getSharedChannel();

  return {
    locks,
    storage: overrides.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null),
    channel,
    sleep: overrides.sleep ?? ((ms) => new Promise((resolve) => { setTimeout(resolve, ms); })),
    now: overrides.now ?? Date.now,
    tabId: overrides.tabId ?? tabId,
    pollIntervalMs: overrides.pollIntervalMs,
    fallbackWaitMs: overrides.fallbackWaitMs,
    fallbackLockTtlMs: overrides.fallbackLockTtlMs,
    waitForResult: overrides.waitForResult
  };
}

/**
 * Run `task` in one tab only.
 * Prefers navigator.locks.request('lynx-auth-refresh'). The callback return
 * is this tab's result. Sibling tabs that are already waiting receive it on
 * BroadcastChannel and do not POST. Falls back to a storage lock (owner id
 * only) plus the same channel. A reload has no channel message, so it POSTs
 * /auth/refresh under the lock and the browser sends the httpOnly cookie.
 *
 * @param {function(): Promise<object>} task
 * @param {object} [envOverrides] - Test seams. Production callers omit this.
 * @returns {Promise<object>}
 */
export async function withAuthRefreshLock(task, envOverrides) {
  const env = resolveEnv(envOverrides);
  const heard = listenForPeer(env.channel);

  try {
    if (env.locks && typeof env.locks.request === 'function') {
      let callbackStarted = false;
      try {
        return await env.locks.request(AUTH_REFRESH_LOCK_NAME, async () => {
          callbackStarted = true;
          return runLeader(task, env, heard);
        });
      } catch (error) {
        if (callbackStarted) throw error;
      }
    }

    return await withBroadcastAndStorageFallback(task, env, heard);
  } finally {
    heard.stop();
  }
}
